import { useCallback, useEffect, useState } from 'react';

import { AuthRedirectError } from '@/api/useAuthedClient';
import { RestError, useAuthedRest } from '@/api/useAuthedRest';

import { useGraphQLQuery } from './useGraphQLQuery';

// Mirrors CrawlRunListRow from the API
// (apps/api-cf/src/backstage/sourcing/crawler/crawler.repository.ts). Timestamps arrive as ISO
// strings over JSON. `trigger` is "cron" | "manual"; `status` is
// "queued" | "running" | "ok" | "failed".
export interface CrawlRun {
  id: string;
  batchId: string;
  roasterId: string;
  roasterName: string;
  trigger: string;
  status: string;
  startedAt: string;
  finishedAt: string | null;
  pagesFetched: number;
  productsSeen: number;
  listingsCreated: number;
  listingsChanged: number;
  listingsDelisted: number;
  // Listings handed to the rest of the pipeline, and how many have come out the
  // far end. Null on runs that predate the counters — no progress to show.
  listingsQueued: number | null;
  listingsDone: number | null;
  warnings: string[];
  error: string | null;
}

// A run the pipeline has not finished with, so the feed keeps polling.
export function isRunActive(run: CrawlRun): boolean {
  return run.status === 'queued' || run.status === 'running';
}

const POLL_INTERVAL_MS = 5000;

interface UseCrawlRunsReturn {
  runs: CrawlRun[] | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<CrawlRun[] | null>;
}

// Recent crawl runs (30-day window, newest first) for the ops feed. Reuses the
// transport-agnostic useGraphQLQuery machinery over the REST admin endpoint,
// exactly like useRoasters.
//
// A crawl now finishes off-request, so the feed polls itself while any run is
// still in flight and falls silent once they are all terminal. `pollIntervalMs`
// is a parameter only so tests need not wait out the real one.
export function useCrawlRuns(pollIntervalMs: number = POLL_INTERVAL_MS): UseCrawlRunsReturn {
  const rest = useAuthedRest();

  const { data, isLoading, error, refetch } = useGraphQLQuery<CrawlRun[]>({
    fetcher: (signal) => rest.request<CrawlRun[]>('/admin/crawl/runs', { signal }),
    deps: [],
  });

  const polling = (data ?? []).some(isRunActive);

  useEffect(() => {
    if (!polling) return;
    const timer = setInterval(() => void refetch(), pollIntervalMs);
    return () => clearInterval(timer);
  }, [polling, pollIntervalMs, refetch]);

  return { runs: data, isLoading, error, refetch };
}

const GENERIC_CRAWL_ERROR = "Couldn't start the crawl — try again";

// The server's own message is worth showing for the two refusals an admin can
// act on: 400 (roaster not crawlable) and 409 (a crawl already in flight).
const MESSAGE_STATUSES = new Set([400, 409]);

// `message: null` means an Auth0 redirect is in flight — render no error.
export type RunCrawlResult = { ok: true; runId: string } | { ok: false; message: string | null };

interface UseRunCrawlReturn {
  runCrawl: (roasterId: string) => Promise<RunCrawlResult>;
  isSubmitting: boolean;
}

// Queues a manual crawl for one roaster. The endpoint validates and enqueues,
// so this resolves once the run is accepted — not once it has finished; the
// runs feed reports the rest.
export function useRunCrawl(): UseRunCrawlReturn {
  const rest = useAuthedRest();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const runCrawl = useCallback(
    async (roasterId: string): Promise<RunCrawlResult> => {
      setIsSubmitting(true);
      try {
        const { runId } = await rest.request<{ runId: string }>(`/admin/crawl/${roasterId}`, {
          method: 'POST',
        });
        return { ok: true, runId };
      } catch (err) {
        if (err instanceof AuthRedirectError) {
          return { ok: false, message: null };
        }
        if (err instanceof RestError && MESSAGE_STATUSES.has(err.status)) {
          return { ok: false, message: err.message || GENERIC_CRAWL_ERROR };
        }
        return { ok: false, message: GENERIC_CRAWL_ERROR };
      } finally {
        setIsSubmitting(false);
      }
    },
    [rest],
  );

  return { runCrawl, isSubmitting };
}
